import { useCallback, useEffect, useMemo } from "react";

import { useQuery } from "hooks";

import {
  toClientCardNextWorkflow,
  toConditionsGroupEvaluable,
} from "scenes/Hubly/components/Workspace/api";
import toGlobalId from "scenes/Hubly/components/Workspace/api/helper";

export function useClientCardNextWorkflowsQuery(clientId, onDone) {
  const queryFactory = useCallback(() => {
    const query = `
      fragment NextWorkflowFields on NextWorkflow {
        uuid
        nextWorkflow {
          uuid
          name
          multipleTimePerClient
        }
        clientWorkflow {
          uuid
        }
        createdAt
        startDate
        task {
          uuid
          title
          assignedAdvisor {
            uuid
            firstName
            lastName
          }
          reminders {
            edges {
              node {
                uuid
                time
                futureReminderNumber
                futureReminderType
                futureReminderTime
                task {
                  uuid
                }
              }
            }
          }
        }
      }

      query getClientWorkflows($id: ID!) {
        client(id: $id) {
          workflows {
            edges {
              node {
                uuid
                name
                archived
                completed
                createdAt
                completedAt
                hideCompletedTasks
                isHidden
                assignedAdvisor {
                  uuid
                  firstName
                  lastName
                }
                previousWorkflow
                startedBy {
                  operatingEntityId
                  operationType
                  startedByText
                }
                workflow {
                  uuid
                  name
                }
                client {
                  uuid
                }
                tasks {
                  edges {
                    node {
                      uuid
                      completed
                    }
                  }
                }
                nextWorkflowsGroup {
                  ... on ConditionsGroupEvaluable {
                    id
                    nextworkflow {
                      edges {
                        node {
                          ...NextWorkflowFields
                          conditionStatus
                        }
                      }
                    }
                    conditions(first: 10) {
                      edges {
                        node {
                          id
                          operator
                          operand
                          type
                          name
                          datePart
                          workflowPart
                          formFieldValue {
                            name
                            type {
                              id
                            }
                          }
                          value {
                            ... on StringValue {
                              stringValue
                            }
                            ... on ArrayStringValue {
                              arrayStringValue
                            }
                          }
                          tags {
                            edges {
                              node {
                                id
                                name
                              }
                            }
                          }
                          streams {
                            edges {
                              node {
                                id
                                name
                                color
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  ... on NextWorkflow {
                    ...NextWorkflowFields
                  }
                }
              }
            }
          }
        }
      }`;

    const resolver = ({ data: { client } }) => {
      return {
        clientWorkflows: client.workflows.edges.map(({ node }) => {
          return {
            ...node,
            id: node.uuid,
            clientId: node.client.uuid,
            workflowId: node.workflow.uuid,
            assignedAdvisorId: node.assignedAdvisor
              ? node.assignedAdvisor.uuid
              : undefined,
            assignedAdvisor: node.assignedAdvisor
              ? { ...node.assignedAdvisor, id: node.assignedAdvisor.uuid }
              : undefined,
            startedBy: node.startedBy
              ? {
                  operationType: node.startedBy.operationType,
                  operatingEntityId: node.startedBy.operatingEntityId,
                }
              : undefined,
            startedByText: node.startedBy?.startedByText,
            tasks: node.tasks.edges.map(({ node: tNode }) => {
              return { ...tNode, id: tNode.uuid };
            }),
            nextWorkflows: node.nextWorkflowsGroup
              .filter((group) => {
                return !group?.conditions;
              })
              .map((nextWorkflow) => {
                return toClientCardNextWorkflow(nextWorkflow);
              }),
            nextWorkflowsGroup: node.nextWorkflowsGroup.map((group) => {
              return group?.conditions
                ? toConditionsGroupEvaluable(group)
                : toClientCardNextWorkflow(group);
            }),
          };
        }),
      };
    };

    return { query, resolver };
  }, []);

  const {
    reloadData,
    data: { clientWorkflows = [] },
    loading,
  } = useQuery(queryFactory, {
    variables: useMemo(() => {
      return { id: toGlobalId("Client", clientId) };
    }, [clientId]),
    options: { fetchOnInit: false },
  });

  useEffect(() => {
    if (!loading && clientWorkflows.length) {
      onDone(clientWorkflows);
    }
  }, [loading, clientWorkflows, onDone]);

  return { reloadData, loading };
}

export default useClientCardNextWorkflowsQuery;
